import React from 'react';
import PropTypes from 'prop-types';
import { Button, Tooltip } from 'antd';
import { MonitorCog } from 'lucide-react';

/**
 * Links from the admin web app into the operator app (React Native web build).
 *
 * The inspection station console lives in the operator app only; the admin UI opens it in a
 * new tab at `${VITE_OPERATOR_APP_URL}/inspection/station?batch=…&operation=…&stage=…`.
 * `batch` is the batch number, `operation` the operation id, `stage` an inspection stage code
 * (VI_FIRST, VI_SECOND, RE_INSPECTION, STOPPER_POSITION, AQL…), `lot_no` optional.
 */
export const OPERATOR_APP_URL = (import.meta.env.VITE_OPERATOR_APP_URL || 'http://localhost:8090').replace(/\/+$/, '');

const query = (params) => {
  const q = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : '';
};

/** Absolute operator-app URL for the inspection station console. */
export const inspectionStationUrl = ({ batch, operation, stage, lot_no } = {}) =>
  `${OPERATOR_APP_URL}/inspection/station${query({ batch, operation, stage, lot_no })}`;

/** Absolute operator-app URL for the filling-lots list of a batch. */
export const inspectionLotsUrl = (batch) => `${OPERATOR_APP_URL}/inspection/lots/${encodeURIComponent(batch ?? '')}`;

/** Opens an operator-app URL in a new tab (noopener). */
export const openOperatorApp = (url) => window.open(url, '_blank', 'noopener,noreferrer');

export const openInspectionStation = (params) => openOperatorApp(inspectionStationUrl(params));

/**
 * Button that opens the operator app's inspection station console in a new tab.
 * Pass `iconOnly` for table cells; any other antd Button props are forwarded.
 */
export function OperatorAppLink({ batch, operation, stage, lot_no, iconOnly = false, label = 'Station console', title, ...rest }) {
  const url = inspectionStationUrl({ batch, operation, stage, lot_no });
  const btn = (
    <Button
      size="small"
      icon={<MonitorCog size={14} />}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); openOperatorApp(url); }}
      aria-label={label}
      {...rest}
    >
      {iconOnly ? null : label}
    </Button>
  );
  return <Tooltip title={title || `Open ${label.toLowerCase()} in the operator app (new tab)`}>{btn}</Tooltip>;
}

OperatorAppLink.propTypes = {
  batch: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  operation: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  stage: PropTypes.string,
  lot_no: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  iconOnly: PropTypes.bool,
  label: PropTypes.string,
  title: PropTypes.string,
};

export default OperatorAppLink;
